import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus } from 'lucide-react';
import { TaskCard, type TagLookup, type TaskCardData } from '@/components/TaskCard';
import { cn } from '@/lib/utils';

export type SwimlaneCategory = 'todo' | 'in_progress' | 'complete';

export interface BoardSwimlaneProps {
  category: SwimlaneCategory;
  label: string;
  workspaceSlug: string;
  tasks: TaskCardData[];
  tagLookup: TagLookup;
  /** ID of the task currently being dragged (so we can render it as ghost). */
  activeTaskId: string | null;
  /**
   * Async create handler. When provided, the swimlane renders a
   * `+` button in the header that opens an inline composer at the
   * bottom of the column. `null` hides the affordance.
   */
  onCreateTask?: ((title: string) => Promise<void>) | null;
  /**
   * Total number of top-level tasks across all swimlanes. Used to
   * compute each column's progress fill — the prototype shows
   * `tasksHere.length / totalCount` for ALL three columns (not a
   * special complete-column behavior).
   */
  totalTaskCount: number;
  /**
   * Controlled composer state. Lifted to the page so the topbar's
   * "New task" CTA can open the To Do composer (matches prototype).
   */
  isComposing: boolean;
  onStartComposing: () => void;
  onStopComposing: () => void;
}

// ── Tint and status colour lookups ────────────────────────────────────

const COLUMN_GRADIENT_VAR: Record<SwimlaneCategory, string> = {
  // Each column reads its tint via a CSS variable applied inline at
  // the section root, then composes the gradient as a background
  // shorthand. Keeps the gradient definition one place per category.
  todo: 'var(--tint-todo)',
  in_progress: 'var(--tint-in-progress)',
  complete: 'var(--tint-done)',
};

const STATUS_COLOR_FOR: Record<SwimlaneCategory, string> = {
  todo: 'var(--status-todo)',
  in_progress: 'var(--status-in-progress)',
  complete: 'var(--status-complete)',
};

/**
 * Glass redesign — Kanban column.
 *
 * Mirrors the prototype's `.v1-col` exactly:
 *   - 16px border-radius, 1px translucent border
 *   - backdrop-filter: blur(14px) saturate(110%)
 *   - tinted gradient header fading to the column body fill
 *   - column head: dot (8px, no halo) + uppercase label + count
 *     PILL + `+` icon button
 *   - 3px progress bar with width = (this column's count / total)
 *     for ALL three columns
 *   - column body: 8px 12px 14px padding, gap 10px between cards
 *   - "+ Add task" dashed CTA at the bottom (replaced by composer)
 *   - drop-target state: brand-orange border + inset glow
 */
export const BoardSwimlane = ({
  category,
  label,
  workspaceSlug,
  tasks,
  tagLookup,
  activeTaskId,
  onCreateTask,
  totalTaskCount,
  isComposing,
  onStartComposing,
  onStopComposing,
}: BoardSwimlaneProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `swimlane:${category}`,
    data: { swimlane: category, type: 'swimlane' },
  });

  const tint = COLUMN_GRADIENT_VAR[category];
  const dotColor = STATUS_COLOR_FOR[category];

  // Prototype: every column shows ITS share of total. No special
  // logic for the complete column.
  const progress = totalTaskCount === 0 ? 0 : Math.round((tasks.length / totalTaskCount) * 100);

  return (
    <section
      data-cat={category}
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border',
        'border-[var(--glass-column-border)]',
        // backdrop-filter: blur(14px) saturate(110%) per prototype.
        '[backdrop-filter:blur(14px)_saturate(110%)]',
        // Tinted gradient via inline CSS variable.
        '[background:linear-gradient(180deg,var(--column-tint),var(--glass-column-bg)_60%)]',
        // Drop-target state.
        isOver && [
          'border-[var(--brand-accent)]',
          'shadow-[0_0_0_1px_var(--brand-accent)_inset,0_8px_32px_oklch(0.74_0.16_50/0.18)]',
        ],
      )}
      style={{ '--column-tint': tint } as React.CSSProperties}
    >
      {/* Header — padding 14px 14px 10px per prototype. */}
      <header className="px-3.5 pt-3.5 pb-2.5">
        <div className="mb-2.5 flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/*
              8×8 dot — no halo. The prototype's `.v1-col-dot` has
              `box-shadow: 0 0 0 3px currentColor; color: transparent`
              which renders as no visible halo (currentColor is
              transparent). Matching that.
            */}
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: dotColor }}
            />
            <h2 className="text-foreground m-0 truncate text-[11.5px] font-semibold tracking-[0.04em] uppercase whitespace-nowrap">
              {label}
            </h2>
            {/* Count pill: bg `--surface-quiet`, padding 1px 7px, fully round. */}
            <span className="text-foreground-muted rounded-full bg-[var(--surface-quiet)] px-[7px] py-px text-[11px] font-mono tabular-nums">
              {tasks.length}
            </span>
          </div>
          {onCreateTask && !isComposing ? (
            <button
              type="button"
              aria-label={`Add task to ${label}`}
              onClick={onStartComposing}
              className={cn(
                'text-foreground-muted inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors',
                'hover:bg-[oklch(0_0_0/0.05)] hover:text-foreground',
                'dark:hover:bg-[oklch(1_0_0/0.06)]',
              )}
            >
              <Plus className="size-3.5" />
            </button>
          ) : null}
        </div>

        {/* Progress bar — 3px tall, trough = `--surface-quiet`,
            fill = the lane's category color. The prototype's
            `box-shadow: 0 0 8px currentColor` glows in the same
            color when we set the fill's color to the category. */}
        <div
          className="h-[3px] overflow-hidden rounded-full bg-[var(--surface-quiet)]"
          aria-hidden="true"
        >
          <div
            className="h-full rounded-full transition-[width] duration-[600ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]"
            style={{
              width: `${progress}%`,
              background: dotColor,
              color: dotColor,
              boxShadow: '0 0 8px currentColor',
            }}
          />
        </div>
      </header>

      {/* Body — padding 8px 12px 14px, gap 10px between cards. */}
      <div
        ref={setNodeRef}
        className="flex flex-1 flex-col gap-2.5 overflow-y-auto px-3 pt-2 pb-3.5"
      >
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((t) => (
            <SortableTaskCard
              key={t.id}
              task={t}
              workspaceSlug={workspaceSlug}
              tagLookup={tagLookup}
              isGhost={activeTaskId === t.id}
            />
          ))}
        </SortableContext>

        {/*
          "+ Add task" CTA OR composer. Always present at the bottom
          of every column per prototype — the "+" in the header is a
          shortcut, not the only entry point.
        */}
        {isComposing && onCreateTask ? (
          <InlineComposer
            onSubmit={onCreateTask}
            onClose={onStopComposing}
          />
        ) : onCreateTask ? (
          <button
            type="button"
            onClick={onStartComposing}
            className={cn(
              'text-foreground-muted mt-1 flex items-center justify-center gap-1.5 rounded-[10px] border border-dashed py-2.5 text-[12px] transition-all',
              'border-[var(--glass-card-border)] bg-transparent',
              'hover:border-foreground-faint hover:text-foreground',
            )}
          >
            <Plus className="size-3.5" />
            Add task
          </button>
        ) : null}
      </div>
    </section>
  );
};

// ── Inline composer (matches prototype's `.v1-composer`) ─────────────

interface InlineComposerProps {
  onSubmit: (title: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Trello / Linear-style in-column composer. After a successful
 * submit the input clears and stays open; Esc or outside-click
 * closes (saving any draft).
 *
 * Glass surface matches `.v1-composer`:
 *   - Card-translucent background, glass border, 12px radius
 *   - 10px 12px padding
 *   - Bottom row: dashed top divider, kbd hints, "Done" cancel
 *   - 13.5px input text, no input border
 */
const InlineComposer = ({ onSubmit, onClose }: InlineComposerProps) => {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Outside-click closes (and saves if there's a draft). Capture-phase
  // listener so the click on a sibling "+" gets a clean close before
  // the new composer opens.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const node = inputRef.current?.closest('[data-composer-root]');
      if (node && !node.contains(e.target as Node)) {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          void submit(trimmed, { keepOpen: false });
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onClose]);

  const submit = async (
    title: string,
    opts: { keepOpen: boolean } = { keepOpen: true },
  ): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      await onSubmit(title);
      setValue('');
      if (!opts.keepOpen) onClose();
      else inputRef.current?.focus();
    } finally {
      setPending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = value.trim();
      if (trimmed.length > 0) void submit(trimmed);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      data-composer-root
      className={cn(
        'rounded-xl border px-3 py-2.5',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
        'shadow-[var(--glass-card-shadow)]',
      )}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Task title…"
        autoFocus
        disabled={pending}
        className={cn(
          'block w-full border-0 bg-transparent px-0 py-1 text-[13.5px] outline-none',
          'placeholder:text-foreground-faint',
          'text-foreground',
        )}
      />
      <div
        className={cn(
          'text-foreground-faint mt-2 flex items-center justify-between border-t border-dashed pt-2 text-[11px]',
          'border-[var(--glass-card-border)]',
        )}
      >
        <span>
          <kbd className="border-border bg-secondary text-foreground-faint mx-0.5 inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]">
            Enter
          </kbd>{' '}
          to add ·{' '}
          <kbd className="border-border bg-secondary text-foreground-faint mx-0.5 inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]">
            Esc
          </kbd>{' '}
          to close
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-foreground-muted hover:text-foreground border-0 bg-transparent text-[11px]"
        >
          Done
        </button>
      </div>
    </div>
  );
};

// ── Sortable + DragOverlay wrappers ───────────────────────────────────

interface SortableTaskCardProps {
  task: TaskCardData;
  workspaceSlug: string;
  tagLookup: TagLookup;
  isGhost: boolean;
}

const SortableTaskCard = ({ task, workspaceSlug, tagLookup, isGhost }: SortableTaskCardProps) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { type: 'task', task },
  });

  return (
    <TaskCard
      ref={setNodeRef}
      task={task}
      workspaceSlug={workspaceSlug}
      tagLookup={tagLookup}
      isDragGhost={isGhost || isDragging}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
    />
  );
};

/**
 * Wrapper used in the BoardPage's `<DragOverlay>` portal — the
 * lifted/tilted card following the cursor.
 */
export const BoardSwimlaneDragOverlay = ({
  task,
  workspaceSlug,
  tagLookup,
}: {
  task: TaskCardData;
  workspaceSlug: string;
  tagLookup: TagLookup;
}): ReactNode => (
  <TaskCard
    task={task}
    workspaceSlug={workspaceSlug}
    tagLookup={tagLookup}
    isDragOverlay
  />
);
